import { Flex, Toaster, FloraButton } from '@grupoboticario/flora-react';
import { TwoPiledSquaresIcon } from '@grupoboticario/flora-react-icons';

import { ProgressBar, ResponsiveFloraTypography, Skeleton } from 'components';
import config from 'config';
import { copyText, getFormattedPrice } from 'helpers';
import { useResellerCredit, useResellerDetail } from 'hooks';
import { newRelicMonitoringService } from 'services';

import Styles from './Summary.styles';
import ProfileComponentsStyles from '../Profile.components.styles';
import { ResetPassword } from '../ResetPassword';

const CREDIT_DEFAULT_VALUE = 0;

export const Summary = () => {
  const { data: credit, isLoading: isLoadingCredit, isError: isErrorCredit } = useResellerCredit();
  const { data: detail, isLoading: isLoadingDetail } = useResellerDetail();
  const isLoading = isLoadingCredit && isLoadingDetail;

  const handleCopy = () => {
    if (detail) {
      copyText(detail.reseller.resellerId)
        .then(() => {
          Toaster.notify({
            kind: 'success',
            description: `Código de ${config.communication.userNamingConvention.singular.toLowerCase()} copiado com sucesso!`,
            origin: 'right-top',
          });
        })
        .catch((e) => {
          Toaster.notify({
            kind: 'error',
            description: `Não foi possível copiar o Código de ${config.communication.userNamingConvention.singular.toLowerCase()}. Tente novamente.`,
            origin: 'right-top',
          });

          newRelicMonitoringService.addPageAction('callback-copiar-codigo-representante', {
            cd_interaction_detail: `erro:${e.message}`,
          });
        });
    }
  };

  return (
    <Styles.Wrapper justify='space-between' gap='$4'>
      <Styles.InfoWrapper direction='column' gap='$2'>
        <ResponsiveFloraTypography fontSize='title' as='h2'>
          {isLoading ? (
            <Skeleton data-testid='skeleton-summary' height='$10' width='50%' />
          ) : (
            <Styles.ResellerName fontWeight='bold'>
              {detail?.reseller.resellerName.toLowerCase()}
            </Styles.ResellerName>
          )}
        </ResponsiveFloraTypography>

        <Flex gap='$2' align='end'>
          {isLoading ? (
            <Skeleton data-testid='skeleton-summary' height='$5' width='75%' />
          ) : (
            <ResponsiveFloraTypography>
              Código de {config.communication.userNamingConvention.singular.toLowerCase()}:
              <Styles.ResellerId>
                <ProfileComponentsStyles.SubText>
                  {detail?.reseller.resellerId}
                </ProfileComponentsStyles.SubText>
              </Styles.ResellerId>
            </ResponsiveFloraTypography>
          )}

          {!isLoading && (
            <FloraButton
              autotagging-flora='on'
              data-flora-text='copiar-codigo'
              data-flora='button'
              size='small'
              onClick={handleCopy}
            >
              <Flex gap='$1' justify='center'>
                <TwoPiledSquaresIcon size={16} /> Copiar
              </Flex>
            </FloraButton>
          )}
        </Flex>

        <ResetPassword eventName='interaction_perfil' />
      </Styles.InfoWrapper>

      <Styles.CreditWrapper>
        {!isErrorCredit ? (
          <Flex direction='column' gap='$2'>
            <ResponsiveFloraTypography>Crédito disponível para compras</ResponsiveFloraTypography>
            {isLoading ? (
              <Skeleton data-testid='skeleton-summary' height='$10' width='25%' />
            ) : (
              <ResponsiveFloraTypography fontSize='title' as='h2' fontWeight='bold'>
                {getFormattedPrice(credit?.available || CREDIT_DEFAULT_VALUE)}
              </ResponsiveFloraTypography>
            )}

            <ProgressBar
              color='primary'
              maxValue={credit?.total || CREDIT_DEFAULT_VALUE}
              size='medium'
              value={credit?.available || CREDIT_DEFAULT_VALUE}
            />
            <Styles.ProgressValues justify='space-between'>
              <Flex gap='$1' align='center'>
                <ProfileComponentsStyles.SubText>Utilizado</ProfileComponentsStyles.SubText>
                {isLoading ? (
                  <Skeleton data-testid='skeleton-summary' height='$4' width='$20' />
                ) : (
                  <ResponsiveFloraTypography>
                    {getFormattedPrice(credit?.notOverdue || CREDIT_DEFAULT_VALUE)}
                  </ResponsiveFloraTypography>
                )}
              </Flex>
              <Flex gap='$1' align='center'>
                <ProfileComponentsStyles.SubText>Total</ProfileComponentsStyles.SubText>
                {isLoading ? (
                  <Skeleton data-testid='skeleton-summary' height='$4' width='$20' />
                ) : (
                  <ResponsiveFloraTypography>
                    {getFormattedPrice(credit?.total || CREDIT_DEFAULT_VALUE)}
                  </ResponsiveFloraTypography>
                )}
              </Flex>
            </Styles.ProgressValues>
          </Flex>
        ) : null}
      </Styles.CreditWrapper>
    </Styles.Wrapper>
  );
};
